import { useCallback, useEffect, useRef, useState } from 'react';

import ReactPlayerLoader from '@brightcove/react-player-loader';

import { IconMuted, IconPause, IconPlay, IconVolume } from '@/assets/icons';
import { BRIGHTCOVE_ACCOUNT_ID } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { sentryService } from '@/services/index';

interface VideoPlayerProps {
  videoId: string;
  accountId?: string;
  showVideoPlayer: boolean;
  autoplay?: boolean;
  loopVideo?: boolean;
  showMute?: boolean;
  setShowVideoPlayer: (value: boolean) => void;
  handleVideoEnd?: (value: boolean) => void;
}

export const VideoPlayer: React.FC<React.HTMLAttributes<HTMLDivElement> & VideoPlayerProps> = ({
  className,
  videoId,
  accountId = BRIGHTCOVE_ACCOUNT_ID,
  showVideoPlayer,
  autoplay = true,
  loopVideo = false,
  showMute = true,
  setShowVideoPlayer,
  handleVideoEnd,
  ...props
}) => {
  const [isPaused, setIsPaused] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isSectionVisible, setSectionIsVisible] = useState(false); // Track if the section is visible
  const [isPlayerReady, setIsPlayerReady] = useState(false);

  const playerRef = useRef<any>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null); // Reference to the section div

  const shouldRenderPlayer = showVideoPlayer || (autoplay && isSectionVisible);

  const handlePlayError = useCallback((error: unknown) => {
    if (error instanceof Error && error.name === 'AbortError') {
      return;
    }

    setIsPaused(true);
    sentryService.captureException(error);
  }, []);

  const attemptPlayback = useCallback(() => {
    const player = playerRef.current?.player;
    if (!player?.play) return;

    try {
      void Promise.resolve(player.play()).catch(handlePlayError);
    } catch (error) {
      handlePlayError(error);
    }
  }, [handlePlayError]);

  const togglePause = () => {
    if (playerRef.current?.player) {
      if (isPaused) {
        attemptPlayback();
      } else {
        playerRef.current.player.pause();
      }
      setIsPaused(!isPaused);
    }
  };

  const toggleMute = () => {
    if (playerRef.current?.player) {
      playerRef.current.player.muted(!isMuted);
      setIsMuted(!isMuted);
    }
  };

  const handleSuccess = (success: any) => {
    let player = success.ref;
    if (player) {
      player.player().controlBar.hide();
    }
    setIsPlayerReady(true);
  };

  useEffect(() => {
    if (autoplay) {
      const sectionDiv = sectionRef.current;
      // Create an intersection observer to detect when the section is fully visible
      const observer = new IntersectionObserver(
        entries => {
          // Check if the section is fully visible in the viewport
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setSectionIsVisible(true); // Show the video
            } else {
              setSectionIsVisible(false); // Show the image
              setShowVideoPlayer(false);
            }
          });
        },
        {
          threshold: 0.7, // Only trigger when the section is fully visible
        }
      );

      if (sectionDiv) {
        observer.observe(sectionDiv);
      }

      return () => {
        if (sectionDiv) {
          observer.unobserve(sectionDiv);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Play the video after 2 secs the section is visible
  useEffect(() => {
    if (videoId && !showVideoPlayer && isSectionVisible && autoplay) {
      if (isSectionVisible) {
        setShowVideoPlayer(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSectionVisible, autoplay]);

  useEffect(() => {
    const player = playerRef.current?.player;
    if (!player) return;
    if (showVideoPlayer) {
      attemptPlayback();
      setIsPaused(false);
    }
    if (!showVideoPlayer) {
      player.pause();
      setIsPaused(true);
    }
  }, [attemptPlayback, showVideoPlayer, isPlayerReady]);

  // Initialize the Brightcove Player
  useEffect(() => {
    if (!handleVideoEnd || !isPlayerReady) return;
    const player = playerRef.current?.player;
    if (!player) return;
    // Listen for the 'ended' event on the Brightcove player
    player.on('ended', handleVideoEnd);

    // Cleanup event listener on unmount
    return () => {
      player.off('ended', handleVideoEnd);
    };
  }, [handleVideoEnd, isPlayerReady]);

  useEffect(() => {
    if (!shouldRenderPlayer) {
      setIsPlayerReady(false);
    }
  }, [shouldRenderPlayer]);

  return (
    <div
      ref={sectionRef}
      data-testid={`test-video-${videoId}`}
      className={cn(`video-player z-0 w-full`, className)}
      {...props}
    >
      {shouldRenderPlayer && (
        <ReactPlayerLoader
          ref={playerRef}
          clasName="w-full"
          videoId={videoId}
          accountId={accountId}
          onSuccess={handleSuccess}
          controls={false}
          options={{
            muted: true,
            playsinline: true,
            loop: loopVideo,
          }}
          onError={(error: never) => sentryService.captureException(error)}
        />
      )}
      {/* Custom Controls */}
      <div
        className={cn(
          `absolute right-6 top-6 z-50 ${showVideoPlayer ? 'flex' : 'hidden'} w-[50px] flex-col gap-2 text-white md:bottom-[50px] md:right-[50px] md:top-auto`,
          { hidden: !showMute }
        )}
      >
        <button
          type="button"
          onClick={togglePause}
          className={cn('bg-frosted-white h-[50px] w-[50px] content-center rounded-[60px]', {
            hidden: autoplay,
          })}
        >
          {isPaused ? <IconPlay className="mx-auto" /> : <IconPause className="mx-auto" />}
        </button>
        <button
          type="button"
          onClick={toggleMute}
          className="bg-frosted-white h-[50px] w-[50px] content-center rounded-[60px]"
        >
          {isMuted ? <IconMuted className="mx-auto" /> : <IconVolume className="mx-auto" />}
        </button>
      </div>
    </div>
  );
};
